import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    IconPrinter,
    IconUser,
    IconUsersGroup,
    IconCalendar,
    IconReceipt,
    IconArrowLeft,
    IconClock,
    IconCreditCard,
    IconCheck,
    IconStack2,
} from '@tabler/icons-react';
import { usePayments, useReservation } from '@/hooks/api-hooks';
import { formatDayShort, formatPrice, setWebTitle } from '@/lib/format-utils';
import { ServiceTitleStatic } from '@/components/services/service-title';
import { TagIcon } from '@/lib/tag-utils';
import { BookingGroupSize, PaymentMethodBadge, ShowPaxGroup, ShowPerson } from '@/components/snippets';
import { ServiceBooking, Reservation, Person } from '@turizi/shared';
import { localTenant } from '@/services/session-service';
import { RESERVATION_PATH } from '@/lib/navigation';
import ServiceDatePopover from '@/components/service-date-popover';
import SelectTimePopover from '@/components/select-time-popover';

const sortBookingsAsc = (list: ServiceBooking[]): ServiceBooking[] => {
    return [...list].sort((a, b) => {
        const dayA = a.start_day || '';
        const dayB = b.start_day || '';

        if (dayA !== dayB) {
            if (!dayA) return -1;
            if (!dayB) return 1;
            return dayA.localeCompare(dayB);
        }

        const hourA = a.start_hour || '';
        const hourB = b.start_hour || '';

        if (hourA !== hourB) {
            if (!hourA) return -1;
            if (!hourB) return 1;
            return hourA.localeCompare(hourB);
        }

        return a.id - b.id;
    });
};

function BookingRow({
    booking,
    reservation,
    allBookings,
}: {
    booking: ServiceBooking;
    reservation: Reservation;
    allBookings?: ServiceBooking[];
}) {
    const { t } = useTranslation();
    const attrs = (booking.self_attrs || []).filter((a) => a.value !== undefined && a.value !== null && a.value !== '');
    const providerName = booking.selected_provider?.name || null;

    // Resolve child bookings from booking.child_bookings OR from allBookings array
    const rawChildBookings = (booking.child_bookings && booking.child_bookings.length > 0)
        ? booking.child_bookings
        : (allBookings ? allBookings.filter((c) => Number(c.parent_booking_id) === Number(booking.id)) : []);
    const childBookings = sortBookingsAsc(rawChildBookings);

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-1 space-y-1 print:break-inside-avoid">
            {/* Top Row: Icon + Service Title & Provider / #ID */}
            <div className="flex items-start justify-between gap-2 text-xs">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Date & Hour (Only if not a package with child bookings) */}
                    {childBookings.length === 0 && (
                        <div className="flex items-start gap-2">
                            <ServiceDatePopover
                                icon={true}
                                size='xs'
                                serviceCategory={booking.service.category}
                                value={[booking.start_day, booking.end_day]}
                                onChange={() => { }}
                                className='font-bold text-gray-800'
                                disabled={true}
                            />
                            <SelectTimePopover
                                icon={true}
                                size='xs'
                                value={booking.start_hour}
                                onChange={() => { }}
                                disabled={true}
                            />
                        </div>
                    )}
                    <div className='flex items-center gap-2 font-semibold text-xs'>
                        <TagIcon name={booking.category} hideText size="xs" noBackground />
                        {ServiceTitleStatic(booking.service)}
                    </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0 text-[11px] text-slate-400 pt-0.5">
                    {providerName && (
                        <>
                            <span title={t('selected_provider')}>{providerName}</span>
                            <span>·</span>
                        </>
                    )}
                    <span className="font-semibold text-slate-400 ml-0.5">#{booking.id}</span>
                </div>
            </div>

            {/* Bottom Row: Date, Hour, Pax Breakdown, Attributes, Observations & Price */}
            <div className="pl-[125px] flex flex-wrap items-stretch justify-between gap-2 pt-0.5 text-[11px]">
                <div className="flex flex-wrap items-start gap-2 text-slate-600 min-w-0 flex-1">
                    {/* Paxs */}
                    <BookingGroupSize
                        booking={booking}
                        reservation={reservation}
                    />

                    {/* Attributes */}
                    <div className='flex flex-wrap items-start gap-1'>
                        {attrs.map((attr, idx) => (
                            <div key={`${attr.key}-${idx}`} className="bg-slate-50 border border-slate-300 px-1.5 py-0.2 rounded text-[10px] text-slate-700">
                                {String(attr.value)}
                            </div>
                        ))}
                    </div>

                    {/* Static Child Services (if package has no child bookings instantiated yet) */}
                    {childBookings.length === 0 && booking.service?.child_services?.length > 0 && (
                        <div className="flex gap-1 flex-wrap text-xxs font-light">
                            {booking.service.child_services.map((child: any) => (
                                <div key={child.id} className="text-slate-800 font-light bg-slate-100 rounded-full px-2 py-0.5">
                                    {ServiceTitleStatic(child)}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Observations */}
                    {booking.observations && (
                        <div className="text-xxs px-2 py-0.5 bg-red-50/50 text-gray-800 rounded-lg border border-red-100 max-w-full break-words">
                            {booking.observations}
                        </div>
                    )}
                </div>

                {/* Price */}
                {booking.money_in_amount != null && Number(booking.money_in_amount) > 0 && (
                    <div className="self-end mt-auto flex flex-col items-end justify-end text-right shrink-0 text-xs font-semibold text-slate-900">
                        $ {formatPrice(Number(booking.money_in_amount))} {booking.money_in_currency || '**'}
                    </div>
                )}
            </div>

            {/* Child Bookings (Service Packages / Groups) */}
            {childBookings.length > 0 && (
                <div className="space-y-1.5 mt-2 pt-1">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        {t('included_services')}
                    </div>
                    <div className="space-y-1.5">
                        {childBookings.map((child) => (
                            <BookingRow key={child.id} booking={child} reservation={reservation} allBookings={allBookings} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

const getBookingExactServiceKey = (booking: ServiceBooking): string => {
    const serviceId = booking.service_id ?? booking.service_data?.id ?? 'none';
    const startDay = booking.start_day || '';
    const endDay = booking.end_day || '';
    const startHour = booking.start_hour || '';
    const endHour = booking.end_hour || '';
    const providerId = booking.selected_provider_id ?? booking.service_data?.providers?.[0]?.id ?? '';

    const sortedBookingAttrs = (booking.self_attrs || [])
        .map(a => `${a.key}:${a.value}`)
        .sort()
        .join(',');

    const sortedServiceAttrs = (booking.service_data?.self_attrs || [])
        .map(a => `${a.key}:${a.value}`)
        .sort()
        .join(',');

    return `${serviceId}|${startDay}|${endDay}|${startHour}|${endHour}|${providerId}|${sortedBookingAttrs}|${sortedServiceAttrs}`;
};

type PrintBookingCluster =
    | { type: 'single'; booking: ServiceBooking }
    | { type: 'group'; key: string; bookings: ServiceBooking[] };

const buildPrintClusters = (bookingsList: ServiceBooking[]): PrintBookingCluster[] => {
    const clusters: PrintBookingCluster[] = [];
    const groupMap = new Map<string, ServiceBooking[]>();
    const groupOrder: string[] = [];

    bookingsList.forEach((b) => {
        const key = getBookingExactServiceKey(b);
        if (!groupMap.has(key)) {
            groupMap.set(key, []);
            groupOrder.push(key);
        }
        groupMap.get(key)!.push(b);
    });

    groupOrder.forEach((key) => {
        const items = groupMap.get(key)!;
        if (items.length > 4) {
            clusters.push({
                type: 'group',
                key,
                bookings: items,
            });
        } else {
            items.forEach((b) => {
                clusters.push({
                    type: 'single',
                    booking: b,
                });
            });
        }
    });

    return clusters;
};

function GroupedBookingRow({
    bookings,
}: {
    bookings: ServiceBooking[];
}) {
    const { t } = useTranslation();
    const firstBooking = bookings[0];
    const serviceObj = firstBooking.service_data || (firstBooking as any)?.service;
    const title = serviceObj ? ServiceTitleStatic(serviceObj) : t(firstBooking.category || 'service');
    const attrs = (firstBooking.self_attrs || []).filter((a) => a.value !== undefined && a.value !== null && a.value !== '');

    const providerName = firstBooking.selected_provider?.name || null;

    const totalPaxs = bookings.reduce(
        (acc, b) => {
            acc.adults += b.adults || 0;
            acc.children += b.children || 0;
            acc.seniors += b.seniors || 0;
            acc.infants += b.infants || 0;
            acc.guests += b.guests || 0;
            return acc;
        },
        { adults: 0, children: 0, seniors: 0, infants: 0, guests: 0 }
    );

    const totalPrice = bookings.reduce((sum, b) => sum + (Number(b.money_in_amount) || 0), 0);
    const priceCurrency = firstBooking.money_in_currency || '';
    const allSamePrice = bookings.every(
        b => Number(b.money_in_amount) === Number(firstBooking.money_in_amount) && b.money_in_currency === firstBooking.money_in_currency
    );

    const ids = bookings.map(b => b.id).filter((id): id is number => typeof id === 'number');
    const minId = ids.length > 0 ? Math.min(...ids) : null;
    const maxId = ids.length > 0 ? Math.max(...ids) : null;

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-1 space-y-1 print:break-inside-avoid">
            {/* Top Row: Date, Hour, Icon, Stacked Badge + Service Title & Provider  / IDs */}
            <div className="flex items-start justify-between gap-2 text-xs">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="flex items-start gap-2 shrink-0">
                        <ServiceDatePopover
                            icon={true}
                            size='xs'
                            serviceCategory={serviceObj?.category}
                            value={[firstBooking.start_day, firstBooking.end_day]}
                            onChange={() => { }}
                            className='font-bold text-gray-800'
                            disabled={true}
                        />
                        <SelectTimePopover
                            icon={true}
                            size='xs'
                            value={firstBooking.start_hour}
                            onChange={() => { }}
                            disabled={true}
                        />
                    </div>
                    <div className="flex items-center gap-2 font-semibold text-xs min-w-0 flex-wrap">
                        <TagIcon name={firstBooking.category || serviceObj?.category} hideText size="xs" noBackground />
                        <span className="font-semibold text-xs">{title}</span>
                        <span className="font-bold text-xxs solid-box-border-dark">
                            x {bookings.length}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0 text-[11px] text-slate-400 pt-0.5">
                    {providerName && (
                        <>
                            <span title={t('selected_provider')}>{providerName}</span>
                            <span>·</span>
                        </>
                    )}
                    <span className="font-semibold text-slate-400 ml-0.5">
                        {minId === maxId ? `#${minId}` : `#${minId} - #${maxId}`}
                    </span>
                </div>
            </div>

            {/* Bottom Row: Combined Pax Breakdown, Attributes & Price */}
            <div className="pl-[125px] flex flex-wrap items-stretch justify-between gap-2 pt-0.5 text-[11px]">
                <div className="flex flex-wrap items-start gap-2 text-slate-600 min-w-0 flex-1">
                    {/* Combined Pax Pill */}
                    <ShowPaxGroup
                        adults={totalPaxs.adults}
                        children={totalPaxs.children}
                        seniors={totalPaxs.seniors}
                        infants={totalPaxs.infants}
                        guests={totalPaxs.guests}
                    />
                    {/* Attributes */}
                    <div className="flex flex-wrap items-start gap-1">
                        {attrs.map((attr, idx) => (
                            <div key={`${attr.key}-${idx}`} className="bg-slate-50 border border-slate-300 px-1.5 py-0.2 rounded text-[10px] text-slate-700">
                                {String(attr.value)}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Price */}
                {totalPrice > 0 && (
                    <div className="self-end mt-auto flex flex-col items-end justify-end text-right shrink-0 text-xs font-semibold text-slate-900">
                        <div>$ {formatPrice(totalPrice)} {priceCurrency}</div>
                        {allSamePrice && bookings.length > 1 && (
                            <div className="text-[10px] text-slate-400 font-normal">
                                ({bookings.length} × $ {formatPrice(Number(firstBooking.money_in_amount))} {priceCurrency})
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function ReservationPrintPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const reservationId = Number(id);

    const { data: resData, isLoading } = useReservation(reservationId);
    const reservation = resData?.data;

    const { data: paymentsRes } = usePayments(
        { reservation_id: reservation?.id },
        { enabled: !!reservation?.id }
    );

    if (reservation) {
        setWebTitle(`${t('reservation_summary')} #${reservation.id} - ${reservation.client_business?.name}`);
    }

    if (isLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="w-7 h-7 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" />
            </div>
        );
    }

    if (!reservation) {
        return (
            <div className="p-8 text-center text-slate-500 font-medium">
                {t('no_data')}
            </div>
        );
    }

    const bookings = reservation.service_bookings || [];

    // Filter top-level bookings and sort by start_day + start_hour ASC
    const sortedTopLevelBookings = sortBookingsAsc(
        bookings.filter((b) => !b.parent_booking_id || !bookings.some((p) => p.id === b.parent_booking_id))
    );

    // Group top-level bookings by service_data.category
    const groupedCategoriesMap = new Map<string, ServiceBooking[]>();
    sortedTopLevelBookings.forEach((b) => {
        const cat = b.service_data?.category || b.service?.category || b.category || 'service';
        if (!groupedCategoriesMap.has(cat)) {
            groupedCategoriesMap.set(cat, []);
        }
        groupedCategoriesMap.get(cat)!.push(b);
    });

    // Sort bookings within each category by start_day + start_hour ASC
    groupedCategoriesMap.forEach((catBookings, cat) => {
        groupedCategoriesMap.set(cat, sortBookingsAsc(catBookings));
    });

    // Sort categories: "service_group" first, "stay" second, then the rest alphabetically
    const sortedCategoryEntries = Array.from(groupedCategoriesMap.entries()).sort(([catA], [catB]) => {
        if (catA === 'service_group') return -1;
        if (catB === 'service_group') return 1;
        if (catA === 'stay') return -1;
        if (catB === 'stay') return 1;
        return catA.localeCompare(catB);
    });

    const allPersons: Person[] = [
        ...(reservation.pax_groups?.flatMap((g) => g.persons || []) || []),
        ...bookings.flatMap((b) => [
            ...(b.pax_groups?.flatMap((pg) => pg.pax_group?.persons || []) || []),
            ...(b.child_bookings?.flatMap((c) => [
                ...(c.pax_groups?.flatMap((pg) => pg.pax_group?.persons || []) || []),
            ]) || []),
        ]),
    ];

    const uniquePersonsMap = new Map<number, Person>();
    allPersons.forEach((p) => {
        const personName = (p.name || '').trim();
        if (!personName || !p.id) return;
        if (!uniquePersonsMap.has(p.id)) {
            uniquePersonsMap.set(p.id, { ...p, name: personName });
        }
    });

    const uniquePersons = Array.from(uniquePersonsMap.values());

    const priceTotals: Record<string, number> = {};
    if (reservation.price_per_currency && Object.keys(reservation.price_per_currency).length > 0) {
        Object.assign(priceTotals, reservation.price_per_currency);
    } else {
        bookings.forEach((b) => {
            if (b.money_in_amount != null && Number(b.money_in_amount) > 0) {
                const curr = b.money_in_currency || 'ARS';
                priceTotals[curr] = (priceTotals[curr] || 0) + Number(b.money_in_amount);
            }
        });
    }

    const tenant = localTenant();
    const logoUrl = tenant?.settings?.logo_url;
    const address = tenant?.settings?.address;
    const city = tenant?.settings?.city;
    const phone = tenant?.settings?.phone;
    const tenantName = tenant?.settings?.store?.display_name || tenant?.name;

    return (
        <div className="max-w-4xl mx-auto p-3 sm:p-5 space-y-2.5 print:p-0 print:max-w-none print:space-y-2 font-sans text-slate-800">
            <style>{`
                @media print {
                    .truncate, [class*="truncate"] {
                        overflow: visible !important;
                        text-overflow: clip !important;
                        white-space: normal !important;
                    }
                }
            `}</style>

            {/* Tools Navbar (Outside printable section, hidden on print) */}
            <div className="sticky top-2 z-20 print:hidden bg-white/95 backdrop-blur-xs flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2.5">
                    <button
                        type="button"
                        onClick={() => {
                            if (window.history.length > 1) {
                                navigate(-1);
                            } else {
                                navigate(RESERVATION_PATH.replace(':id', reservation.id.toString()));
                            }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold shadow-2xs transition-colors cursor-pointer"
                        title={t('back')}
                    >
                        <IconArrowLeft size={14} />
                        <span className="hidden sm:inline">{t('back')}</span>
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => window.print()}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold shadow-xs transition-colors cursor-pointer"
                    >
                        <IconPrinter size={15} />
                        <span>{t('print')}</span>
                    </button>
                </div>
            </div>

            {/* Header & Tenant Info */}
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-2.5 print:pb-2">
                <div className="flex items-center gap-3">
                    {logoUrl && (
                        <img
                            src={logoUrl}
                            alt={tenantName || 'Logo'}
                            className="h-9 sm:h-10 object-contain shrink-0"
                        />
                    )}
                    <div>
                        {tenantName && (
                            <div className="font-extrabold text-slate-900 text-sm sm:text-base leading-tight">
                                {tenantName}
                            </div>
                        )}
                        {(address || city || phone) && (
                            <div className="text-[11px] text-slate-500 font-medium flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                {(address || city) && (
                                    <span>
                                        {[address, city].filter(Boolean).join(', ')}
                                    </span>
                                )}
                                {(address || city) && phone && <span>·</span>}
                                {phone && (
                                    <span>{t('phone')}: {phone}</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Highlighted Client + Reservation ID & Dates Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg">
                <div className="flex flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-4 flex-wrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-900 text-white font-extrabold text-md tracking-tight print:border print:border-slate-900">
                            {t('reservation')} #{reservation.id}
                        </span>
                        <span className="font-extrabold text-slate-900 text-base sm:text-lg leading-tight">
                            <span className='font-light'>{t('client')}:</span> {reservation.client_business?.name}
                        </span>
                    </div>
                    {(reservation?.client_business?.tax_name || reservation?.client_business?.tax_id_number) && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                            {reservation?.client_business?.tax_name && (
                                <span className="font-medium">
                                    {reservation.client_business?.tax_name}
                                </span>
                            )}
                            {reservation?.client_business?.tax_id_number && (
                                <span className="text-slate-500">
                                    <span className="font-semibold uppercase text-slate-400">
                                        {reservation.client_business?.tax_id_type || t('tax_id')}:
                                    </span>{' '}
                                    <span className="text-slate-700 font-medium">{reservation.client_business?.tax_id_number}</span>
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-1.5 text-xs shrink-0">
                    <IconCalendar size={13} className="text-slate-500" />
                    <span className="font-bold text-slate-500 uppercase text-[10px]">{t('date')}:</span>
                    <span className="font-bold text-slate-900">
                        {formatDayShort(reservation.start_day)} - {formatDayShort(reservation.end_day)}
                    </span>
                </div>
            </div>

            {/* Main Two-Column Content: Left = Paxs, Right = Services, Total & Payments */}
            <div>
                {/* Left Column: Minimal & Succinct Pax Groups */}
                {(reservation.pax_groups && reservation.pax_groups.length > 0) || uniquePersons.length > 0 ? (
                    <div>
                        <div className="text-sm font-extrabold text-slate-800">
                            {t('paxs')}
                        </div>
                        {reservation.pax_groups && reservation.pax_groups.length > 0 ? (
                            <div className="flex flex-wrap gap-1 items-start">
                                {reservation.pax_groups.map((group) => (
                                    <div
                                        key={group.id}
                                        className="bg-white p-1 rounded-md border border-gray-200 w-fit"
                                    >
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <IconUsersGroup size={13} className="text-indigo-500 shrink-0" />
                                            <span className="font-semibold text-xs text-slate-900 leading-tight">
                                                {group.name}
                                            </span>
                                            <ShowPaxGroup
                                                size="xs"
                                                infants={group.infants}
                                                adults={group.adults}
                                                children={group.children}
                                                seniors={group.seniors}
                                                guests={group.guests}
                                            />
                                        </div>

                                        {group.persons && group.persons.length > 0 && (
                                            <div className="ml-1.5 pl-1.5 border-l border-slate-200 space-y-0.5">
                                                {group.persons.map((p) => (
                                                    <div key={p.id} className="min-w-0 text-[11px]">
                                                        <ShowPerson person={p} />
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {group.observations && (
                                            <p className="text-[10px] text-slate-500 italic leading-tight">
                                                {group.observations}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {uniquePersons.map((p, idx) => (
                                    <div key={p.id || idx} className="bg-white border border-slate-200 px-2 py-1 rounded-md text-xs min-w-0">
                                        <ShowPerson person={p} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : null}

                {/* Right Column: Services List, Total Summary & Payments */}
                <div className="w-full flex-1 min-w-0 space-y-2.5">
                    {/* Services List */}
                    <div className="space-y-1">
                        {sortedCategoryEntries.map(([categoryKey, catBookings]) => {
                            const clusters = buildPrintClusters(catBookings);

                            return (
                                <div key={categoryKey} className="space-y-1.5">
                                    {/* Category Header */}
                                    <div className="flex items-center gap-2 text-sm font-extrabold text-slate-800">
                                        <span>
                                            {t(`${categoryKey}_plural`)}
                                        </span>
                                        <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-700 rounded-full font-bold">
                                            {catBookings.length}
                                        </span>
                                    </div>

                                    {/* Bookings */}
                                    <div className="space-y-1.5">
                                        {clusters.map((cluster) => {
                                            if (cluster.type === 'single') {
                                                return (
                                                    <div key={cluster.booking.id} className="space-y-1">
                                                        <BookingRow booking={cluster.booking} reservation={reservation} allBookings={bookings} />
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div key={cluster.key} className="space-y-1">
                                                    <GroupedBookingRow bookings={cluster.bookings} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Simple Light Final Price Summary Bar */}
                    <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs print:py-1.5">
                        <div className="flex items-center gap-1.5 font-bold text-slate-700 uppercase tracking-wider text-[11px]">
                            <IconReceipt size={14} className="text-slate-500" />
                            <span>{t('total')}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-md font-bold text-slate-900">
                            {
                                Object.entries(priceTotals).map(([currency, amount]) => (
                                    <div key={currency} className="flex items-baseline gap-1">
                                        <span className="font-bold text-slate-900">
                                            $ {formatPrice(Number(amount))}
                                        </span>
                                        <span className="text-[10px] font-medium text-slate-500">{currency}</span>
                                    </div>
                                ))
                            }
                        </div>
                    </div>

                    {/* Payments List (if any) */}
                    {(paymentsRes?.data || []).length > 0 && (
                        <div className="space-y-1">
                            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                                <IconCreditCard size={12} />
                                <span>{t('payments')}</span>
                            </div>
                            <div className="space-y-1">
                                {(paymentsRes?.data || []).map((pay) => (
                                    <div
                                        key={pay.id}
                                        className="flex items-center justify-between gap-2 text-xs py-1 px-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
                                    >
                                        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                                            <IconCheck size={12} className="text-emerald-600 shrink-0" />
                                            <span className="font-semibold text-slate-800 shrink-0">#{pay.id}</span>
                                            <span className="text-slate-400">·</span>
                                            <span className="text-slate-600 truncate">{formatDayShort(pay.payment_date || pay.created_at)}</span>
                                            {pay.payment_method && (
                                                <>
                                                    <span className="text-slate-400">·</span>
                                                    <PaymentMethodBadge method={pay.payment_method} />
                                                </>
                                            )}
                                        </div>
                                        <div className="font-bold text-slate-900 shrink-0 text-xs">
                                            ${formatPrice(Number(pay.amount || 0))} <span className="text-[10px] font-normal text-slate-500 uppercase">{pay.currency || ''}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
